import { Link } from "react-router";
import { typeFor } from "../../shared/catalog";
import { peopleLine } from "../../shared/contract";
import { relativeDays, summarize, type DashboardItem } from "../../shared/dashboard";
import { formatDate } from "../../shared/dates";
import type { LocalContract } from "../lib/db";
import { undatedActive } from "../lib/display";
import { useContracts, useSession } from "../lib/session";
import { PushBanner } from "./Settings";
import { Alert, Badge, Card, CardTitle, EmptyState, PageHeader, Spinner, buttonClass, cx, focusRing } from "../components/ui";

function Stat({ label, value, to, tone }: { label: string; value: number; to: string; tone?: "warn" | "bad" }) {
  const alert = !!value && !!tone;
  return (
    <Link
      to={to}
      className={cx(
        "group relative overflow-hidden rounded-xl border border-line bg-surface p-4 shadow-xs transition hover:border-line-strong hover:shadow-sm",
        focusRing,
        alert && (tone === "bad" ? "before:bg-bad" : "before:bg-warn"),
        alert && "before:absolute before:inset-y-0 before:left-0 before:w-1",
      )}
    >
      <div className={cx("text-2xl font-semibold tabular-nums tracking-tight", alert ? (tone === "bad" ? "text-bad" : "text-warn") : "text-fg")}>{value}</div>
      <div className="mt-0.5 text-sm leading-snug text-muted group-hover:text-fg">{label}</div>
    </Link>
  );
}

function Row({ item, show }: { item: DashboardItem<LocalContract>; show: "deadline" | "end" }) {
  const c = item.contract;
  const date = show === "deadline" ? item.deadline : item.termEnd;
  const days = show === "deadline" ? item.daysToDeadline : item.daysToEnd;
  return (
    <li>
      <Link to={`/contracts/${c.id}`} className={cx("-mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-subtle", focusRing)}>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-fg">{c.title}</div>
          <div className="truncate text-xs text-faint">{peopleLine(c) || typeFor(c.type)?.label || "No one added yet"}</div>
        </div>
        <div className="text-right text-sm tabular-nums">
          <div className="text-fg">{formatDate(date)}</div>
          <div className={days !== null && days <= 7 ? "text-xs font-semibold text-bad" : "text-xs text-faint"}>{relativeDays(days)}</div>
        </div>
        {c.pending && <Badge>not synced</Badge>}
      </Link>
    </li>
  );
}

const plus = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export function Dashboard() {
  const contracts = useContracts();
  const { today, me } = useSession();
  if (!contracts) return <Spinner />;
  const waiting = me?.inbox?.waiting ?? 0;
  const inboxNote = waiting > 0 && (
    <Alert tone="info">
      {waiting === 1 ? "A contract you emailed is" : `${waiting} contracts you emailed are`} waiting for you to check and save.{" "}
      <Link to="/inbox" className="font-medium underline underline-offset-2">
        Review {waiting === 1 ? "it" : "them"}
      </Link>
    </Alert>
  );

  if (contracts.length === 0) {
    return (
      <div className="space-y-4">
        <PushBanner />
        {inboxNote}
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5M12 11v6M9 14h6" />
            </svg>
          }
          title={<h1 className="text-xl font-semibold tracking-tight text-fg">Add your first contract</h1>}
          action={
            <Link to="/contracts/new" className={cx(buttonClass(), "mt-2")}>
              {plus}
              Add a contract
            </Link>
          }
        >
          Upload a PDF or Word file, or just type the key dates. We'll remind you before every renewal and notice deadline.
        </EmptyState>
      </div>
    );
  }

  const s = summarize(contracts, today);
  // Contracts without an end date never get a reminder, which is easy to miss.
  const undated = undatedActive(contracts);
  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        actions={
          <Link to="/contracts/new" className={buttonClass()}>
            {plus}
            Add contract
          </Link>
        }
      />
      <PushBanner />
      {inboxNote}
      <section aria-label="Summary" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Active" value={s.active.length} to="/contracts?filter=active" />
        <Stat label="Notice due this month" value={s.noticeThisMonth.length} to="/contracts?filter=notice" tone="bad" />
        <Stat label="Ending in 30 days" value={s.expiring30.length} to="/contracts?filter=30" tone="warn" />
        <Stat label="Ending in 31–60 days" value={s.expiring60.length} to="/contracts?filter=60" />
        <Stat label="Ending in 61–90 days" value={s.expiring90.length} to="/contracts?filter=90" />
        <Stat label="Expired" value={s.expired.length} to="/contracts?filter=expired" />
      </section>

      {undated.length > 0 && (
        <p className="text-sm text-muted">
          {undated.length === 1 ? (
            <>
              <Link to={`/contracts/${undated[0]!.id}`} className="font-medium text-fg underline underline-offset-2">
                {undated[0]!.title}
              </Link>{" "}
              has no end date, so it gets no reminders.
            </>
          ) : (
            <>{undated.length} contracts have no end date, so they get no reminders.</>
          )}{" "}
          Add one if the contract has a term or a renewal date.
        </p>
      )}

      {s.noticeMissed.length > 0 && (
        <Card className="space-y-1 border-bad-line">
          <h2 className="text-base font-semibold tracking-tight text-bad">Notice deadline passed</h2>
          <p className="pb-2 text-sm text-muted">The notice window for these has closed, but the term hasn't ended yet. Check whether you can still act.</p>
          <ul>
            {s.noticeMissed.map((i) => (
              <Row key={i.contract.id} item={i} show="end" />
            ))}
          </ul>
        </Card>
      )}

      <Card className="space-y-2">
        <CardTitle>Upcoming notice deadlines</CardTitle>
        {s.upcoming.length === 0 ? (
          <p className="text-sm text-faint">No upcoming deadlines. Contracts without an end date don't have one.</p>
        ) : (
          <ul className="divide-y divide-line">
            {s.upcoming.slice(0, 10).map((i) => (
              <Row key={i.contract.id} item={i} show="deadline" />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
